<template>
  <div
    v-if="cardLayout === 'default'"
    class="row-table w-full justify-center h-[72px] md:h-[105px] overflow-hidden"
    :style="`background: ${colorDefination.bg};`"
  >
    <div :class="`${colorDefination.stripe} w-2 md:w-3`" :style="colorDefination.stripeStyle"></div>
    <div class="flex flex-col p-1.5 md:p-1.5 flex-grow min-w-0">
      <div class="flex justify-start items-center gap-x-1 md:gap-x-1.5">
        <v-avatar
          v-if="stationIsAlert"
          color="white"
          size="21"
          class="shrink-0"
          :class="{ 'mobile-avatar': !mobile }"
        >
          <span class="text-[15px] font-bold text-negative">!</span>
        </v-avatar>
        <v-avatar
          v-else
          color="primary"
          size="21"
          class="shrink-0"
          :class="{ 'mobile-avatar': !mobile }"
          :style="npsRingStyle"
        >
          <ProtectedAvatar :src="resolvedPhoto" :size="21">
            <span class="font-bold" :class="mobile ? 'text-[10px]' : 'text-[.6rem]'">{{
              avatarInitials
            }}</span>
          </ProtectedAvatar>
        </v-avatar>
        <div class="min-w-0 flex-grow">
          <span
            v-if="!ourTeam"
            class="flex items-center justify-center gap-[10px] rounded-2xl mb-[3px] px-2 py-0 font-sans font-normal not-italic leading-[14px] tracking-[0.06px] text-[var(--Tertiary-40)]"
            :class="[
              defineOurTeamBgColor,
              mobile
                ? 'h-[18px] w-fit text-[10px]'
                : 'h-[16px] md:h-[21px] w-[40px] md:w-[53px] text-[8px] md:text-[10px]'
            ]"
            >Externo</span
          >
          <v-tooltip :text="name" location="top">
            <template v-slot:activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="two-line-ellipsis text-left text-gray-800 leading-tight cursor-default"
                :class="[
                  isAlertTextColor,
                  mobile ? 'text-[11px]' : 'text-[9px] md:text-[12px] md:max-w-[100px]'
                ]"
                :style="cardTextStyle"
              >
                {{ name }}
              </span>
            </template>
          </v-tooltip>
        </div>
      </div>
      <div class="flex justify-start gap-x-1 md:gap-x-1.5 -mt-0.5 md:-mt-1">
        <div class="w-[14px] md:w-[20px] h-[8px] md:h-[20px] shrink-0"></div>
        <p
          class="div3 text-left"
          :class="[cardTextClass, mobile ? 'text-[10px]' : 'text-[8px] md:text-[10px]']"
          :style="cardTextStyle"
        >
          {{ crm }}
        </p>
      </div>
      <div class="mt-auto w-full">
        <template v-if="stationIsAlert">
          <div class="w-full h-[14px] md:h-[18px] rounded-sm bg-white/30"></div>
        </template>
        <template v-else>
          <div class="flex justify-start gap-x-1 md:gap-x-1.5">
            <div
              class="group-buttons w-full flex items-center justify-center gap-1 md:gap-2 flex-wrap"
            >
              <v-tooltip v-if="hasObservations" text="Existe observações">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" color="warning" v-bind="props" :icon="mdiTextBoxCheck" />
                </template>
              </v-tooltip>
              <v-tooltip v-if="doctorPending" text="Médico pendente">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" color="primary" v-bind="props" :icon="mdiAccountAlert" />
                </template>
              </v-tooltip>
              <v-tooltip v-if="!doctorConfirmed" text="Plantão pendente">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    color="neutral"
                    v-bind="props"
                    :icon="mdiClockAlertOutline"
                  />
                </template>
              </v-tooltip>
              <v-tooltip v-if="doctorConfirmed" text="Plantão confirmado">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    color="success"
                    v-bind="props"
                    :icon="mdiClockCheckOutline"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="doctorStationStatus !== DoctorStationStatus.SEM_STATUS && !notShowStatusIcon"
                :text="doctorStationStatusTranslate[doctorStationStatus].text"
              >
                <template v-slot:activator="{ props }">
                  <component
                    v-bind="props"
                    :is="doctorStationStatusTranslate[doctorStationStatus].icon"
                  />
                </template>
              </v-tooltip>

              <v-tooltip v-if="exceptionSummaryStatus" :text="exceptionTooltipText">
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    size="small"
                    :icon="exceptionIconPath"
                    :color="exceptionIconColor"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="flags.enabledPostStation && postStationStatus"
                :text="postStationTooltipText"
              >
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    data-testid="post-station-status-icon"
                    size="small"
                    :icon="mdiClockEnd"
                    :color="postStationIconColor"
                  />
                </template>
              </v-tooltip>

              <v-tooltip
                v-if="flags.enabledPermanenceRecord && permanenceTooltip"
                :text="permanenceTooltip"
              >
                <template v-slot:activator="{ props: tooltipProps }">
                  <PermanenceRecordIcon
                    v-bind="tooltipProps"
                    data-testid="permanence-record-icon"
                  />
                </template>
              </v-tooltip>
            </div>
          </div>
        </template>
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
  <!-- Celular v2, nível Semana (spec v2 §8.3): foto maior, ícones no canto, nome e CRM embaixo. -->
  <div
    v-else-if="cardLayout === 'stack'"
    class="flex w-full overflow-hidden"
    :style="`background: ${colorDefination.bg};`"
    data-testid="station-row-stack"
  >
    <div
      :class="`${colorDefination.stripe} w-2 shrink-0`"
      :style="colorDefination.stripeStyle"
    ></div>
    <!-- v2.2: padding vertical de 4 px (horizontal segue 6 px) para encurtar o card. -->
    <div class="flex min-w-0 flex-grow flex-col gap-0.5 px-1.5 py-1">
      <div class="flex items-start justify-between gap-1" data-testid="station-row-avatar-row">
        <v-avatar v-if="stationIsAlert" color="white" :size="layoutAvatarSize" class="shrink-0">
          <span class="font-bold text-negative" :style="initialsStyle">!</span>
        </v-avatar>
        <v-avatar
          v-else
          color="primary"
          :size="layoutAvatarSize"
          class="shrink-0"
          :style="npsRingStyle"
        >
          <ProtectedAvatar :src="resolvedPhoto" :size="layoutAvatarSize">
            <span class="font-bold" :style="initialsStyle">{{ variantInitials }}</span>
          </ProtectedAvatar>
        </v-avatar>
        <div
          v-if="stationIsAlert"
          class="h-[14px] w-6 shrink-0 rounded-sm bg-white/30"
          data-testid="station-row-alert-bar"
        ></div>
      </div>
      <!--
        v2.2: ícones numa linha só abaixo da foto (ao lado dela só caberia 1 de 16 px e a coluna
        empilhada estreitava o nome). Coluna de ~50–61 px (iPhone SE: dia de 71 − 1 borda − 8 faixa
        − 12 padding = 50): 3 × 16 px + 2 × 1 px (gap-px com max) = 50 px → max 3.
      -->
      <StationRowIcons v-if="!stationIsAlert" :icons="statusIcons" :max="STACK_ICONS_MAX" />
      <span
        v-if="!ourTeam"
        class="w-fit rounded-2xl px-2 text-[10px] leading-[16px] text-[var(--Tertiary-40)]"
        :class="defineOurTeamBgColor"
        data-testid="station-row-external"
        >Externo</span
      >
      <span
        class="station-row-name station-row-name--clamp-3 station-row-name--hyphenate text-left leading-tight text-gray-800"
        :class="[
          isAlertTextColor,
          isUppercaseName ? 'station-row-name--caps text-[10.45px]' : 'text-[11px]'
        ]"
        :style="cardTextStyle"
        lang="pt-BR"
        :title="name"
        :aria-label="name"
        data-testid="station-row-name"
        >{{ name }}</span
      >
      <p
        class="text-left text-[10px] leading-tight"
        :class="cardTextClass"
        :style="cardTextStyle"
        data-testid="station-row-crm"
      >
        {{ crm }}
      </p>
      <slot name="footer"></slot>
    </div>
  </div>
  <!-- Celular v2, nível Detalhe: foto à esquerda; nome; CRM e ícones na mesma linha. -->
  <div
    v-else
    class="flex w-full overflow-hidden"
    :style="`background: ${colorDefination.bg};`"
    data-testid="station-row-row"
  >
    <div
      :class="`${colorDefination.stripe} w-2 shrink-0`"
      :style="colorDefination.stripeStyle"
    ></div>
    <div class="flex min-w-0 flex-grow items-center gap-2 p-1.5">
      <v-avatar v-if="stationIsAlert" color="white" :size="layoutAvatarSize" class="shrink-0">
        <span class="font-bold text-negative" :style="initialsStyle">!</span>
      </v-avatar>
      <v-avatar
        v-else
        color="primary"
        :size="layoutAvatarSize"
        class="shrink-0"
        :style="npsRingStyle"
      >
        <ProtectedAvatar :src="resolvedPhoto" :size="layoutAvatarSize">
          <span class="font-bold" :style="initialsStyle">{{ variantInitials }}</span>
        </ProtectedAvatar>
      </v-avatar>
      <div class="flex min-w-0 flex-grow flex-col gap-0.5">
        <span
          v-if="!ourTeam"
          class="w-fit rounded-2xl px-2 text-[10px] leading-[16px] text-[var(--Tertiary-40)]"
          :class="defineOurTeamBgColor"
          data-testid="station-row-external"
          >Externo</span
        >
        <span
          class="station-row-name station-row-name--clamp-2 text-left leading-tight text-gray-800"
          :class="[
            isAlertTextColor,
            isUppercaseName ? 'station-row-name--caps text-[11.4px]' : 'text-[12px]'
          ]"
          :style="cardTextStyle"
          lang="pt-BR"
          :title="name"
          :aria-label="name"
          data-testid="station-row-name"
          >{{ name }}</span
        >
        <p
          class="text-left text-[10px] leading-tight"
          :class="cardTextClass"
          :style="cardTextStyle"
          data-testid="station-row-crm"
        >
          {{ crm }}
        </p>
        <!--
          v2.2: ícones (ou a barra do alerta) numa linha própria abaixo do CRM, para o CRM nunca
          quebrar por causa deles. Coluna de ~77 px: 4 × 16 px + 3 × 1 px (gap-px) = 67 px → max 4.
        -->
        <div
          v-if="stationIsAlert"
          class="h-[14px] w-6 shrink-0 rounded-sm bg-white/30"
          data-testid="station-row-alert-bar"
        ></div>
        <StationRowIcons v-else :icons="statusIcons" :max="ROW_ICONS_MAX" />
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getInitialsChar } from '@/utils/string'
import { nameInitials, stripHonorific } from '@/utils/stationDisplay'
import { resolveAvatarSrc } from '@/utils/avatar'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import PermanenceRecordIcon from '@/assets/icons/checks/permanence_registro.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  mdiTextBoxCheck,
  mdiAccountAlert,
  mdiClockAlertOutline,
  mdiClockCheckOutline,
  mdiClockEnd
} from '@mdi/js'
import {
  DoctorStationStatus,
  doctorStationStatusTranslate
} from '@/translations/doctor.station.status'
import {
  DoctorStationReminderStatus,
  ExceptionSummaryStatus
} from '@/Network/Types/Responses/StationResponses'
import type { PostStationCellStatus } from '@/Network/Types/Responses/StationResponses'
import StationRowIcons from './StationRowIcons.vue'
import {
  useStationRowStyle,
  type StationRowCellType,
  type StationCardLayout
} from './useStationRowStyle'

export type CellTypes = StationRowCellType

const props = withDefaults(
  defineProps<{
    tableType?: CellTypes
    name?: string
    text?: string
    minimal?: boolean
    flat?: boolean
    solid?: boolean
    crm?: string
    success?: boolean
    error?: boolean
    waiting?: boolean
    pendences?: boolean
    photo?: string
    hasObservations?: boolean
    doctorPending?: boolean
    doctorConfirmed?: boolean
    doctorStationStatus?: DoctorStationStatus
    ourTeam?: boolean
    doctorStationReminderStatus?: DoctorStationReminderStatus
    isAlert?: boolean
    requiredCheckNotOurTeam?: boolean
    requiredCheckOurTeam?: boolean
    npsScore?: number | null
    exceptionSummaryStatus?: ExceptionSummaryStatus | null
    postStationStatus?: PostStationCellStatus | null
    permanenceTooltip?: string | null
    colorOverride?: { stripe: string; bg: string } | null
    /** Grade do celular (R1b, `isMobileLayout`): texto ≥ 10px em vez do encolhimento do `max-md`. */
    mobile?: boolean
    /** Layout do card (spec v2 §8.3). `default` = template de hoje, literal (desktop e v1). */
    cardLayout?: StationCardLayout
    /** Só nas variantes `stack`/`row`: sobrescreve a foto (32/42 px). O `default` mantém 21 px. */
    avatarSize?: number
  }>(),
  {
    tableType: 'EXTRA',
    name: '',
    text: '',
    minimal: false,
    flat: false,
    solid: false,
    crm: '',
    success: true,
    error: true,
    waiting: true,
    pendences: true,
    photo: '',
    hasObservations: false,
    doctorPending: false,
    doctorConfirmed: false,
    doctorStationStatus: DoctorStationStatus.SEM_STATUS,
    ourTeam: false,
    doctorStationReminderStatus: DoctorStationReminderStatus.PENDING,
    npsScore: null,
    isAlert: false,
    exceptionSummaryStatus: null,
    postStationStatus: null,
    permanenceTooltip: null,
    colorOverride: null,
    mobile: false,
    cardLayout: 'default',
    avatarSize: undefined
  }
)

const { flags } = useFeatureFlags()

const resolvedPhoto = computed(() => resolveAvatarSrc(props.photo))
/** QA-11: no celular as iniciais saem do nome sem "Dr./Dra." (AS); o desktop segue igual ao main. */
const avatarInitials = computed(() =>
  props.mobile ? nameInitials(stripHonorific(props.name)) : getInitialsChar(props.name)
)

/** Variantes do celular v2: foto maior (decisão 22) e iniciais sem "Dr./Dra." (QA-11 do v1). */
const VARIANT_AVATAR_SIZE: Record<Exclude<StationCardLayout, 'default'>, number> = {
  stack: 32,
  row: 42
}
const layoutAvatarSize = computed(
  () =>
    props.avatarSize ??
    (props.cardLayout === 'default' ? 21 : VARIANT_AVATAR_SIZE[props.cardLayout])
)
const initialsStyle = computed(() => ({
  fontSize: `${Math.max(10, Math.round(layoutAvatarSize.value * 0.38))}px`
}))
/**
 * Espaços de ícone por linha nas variantes (v2.2): acima disso o último vira "+N". Com `max` o
 * gap é de 1 px. Stack: 3 × 16 + 2 × 1 = 50 px, cabe na coluna do iPhone SE (dia de 71 px − 1 de
 * borda − 8 da faixa − 12 de padding = 50 px). Row: 4 × 16 + 3 × 1 = 67 px na coluna de ~77 px.
 */
const STACK_ICONS_MAX = 3
const ROW_ICONS_MAX = 4
// `?? ''`: o withDefaults só cobre `undefined`; um chamador pode mandar `name: null`.
const variantInitials = computed(() => nameInitials(stripHonorific(props.name ?? '')))
/**
 * v2.2: nome todo em CAIXA ALTA (ex.: "INAE CAVALCANTI MARCONDES MACHADO") ocupa mais largura;
 * nas variantes do celular ele sai em 0,95 do tamanho (11 → 10,45 px; 12 → 11,4 px, nunca < 10 px).
 */
const isUppercaseName = computed(() => {
  const name = props.name ?? ''
  return /\p{L}/u.test(name) && name === name.toUpperCase()
})

// Cor, alerta e ícones vêm do composable (reusado pelo StationListCard do celular).
const {
  stationIsAlert,
  npsRingStyle,
  colorDefination,
  defineOurTeamBgColor,
  isAlertTextColor,
  cardTextClass,
  cardTextStyle,
  notShowStatusIcon,
  exceptionIconPath,
  exceptionIconColor,
  exceptionTooltipText,
  postStationIconColor,
  postStationTooltipText,
  statusIcons
} = useStationRowStyle(() => ({
  tableType: props.tableType,
  solid: props.solid,
  ourTeam: props.ourTeam,
  isAlert: props.isAlert,
  colorOverride: props.colorOverride,
  requiredCheckOurTeam: props.requiredCheckOurTeam,
  requiredCheckNotOurTeam: props.requiredCheckNotOurTeam,
  exceptionSummaryStatus: props.exceptionSummaryStatus,
  postStationStatus: props.postStationStatus,
  npsScore: props.npsScore,
  hasObservations: props.hasObservations,
  doctorPending: props.doctorPending,
  doctorConfirmed: props.doctorConfirmed,
  doctorStationStatus: props.doctorStationStatus,
  permanenceTooltip: props.permanenceTooltip
}))
</script>

<style scoped>
.row-table {
  display: flex;
}

.decoration-left {
  width: 18px !important;
}

.btn-green,
.btn-red,
.btn-yellow {
  width: 14px;
  height: 14px;
}

.btn-green {
  border: 1px #198038 solid;
}

.btn-red {
  border: 1px theme('colors.primary10') solid;
}

.btn-yellow {
  border: 1px #f1c21b solid;
}

.group-buttons {
  gap: 1px;
}

.two-line-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}

/*
 * Celular v2 (stack/row): o nome quebra entre palavras. `anywhere` só corta uma palavra quando a
 * linha não tem outro ponto de quebra (palavra maior que a coluna). O corte de linhas vem das
 * classes --clamp-N.
 */
.station-row-name {
  word-break: normal;
  overflow-wrap: anywhere;
  -webkit-hyphens: manual;
  hyphens: manual;
}

/*
 * Só no stack (nível Semana, coluna de ~54–60 px): sobrenomes em caixa alta não cabem na linha,
 * então a hifenização pt-BR (lang="pt-BR") corta na sílaba, com hífen. No row (Detalhe) a coluna
 * é larga e a quebra fica estritamente entre palavras.
 */
.station-row-name--hyphenate {
  -webkit-hyphens: auto;
  hyphens: auto;
}

.station-row-name--clamp-3 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.station-row-name--clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

/* Caixa alta: o tamanho (0,95) vem da classe text-[...] do template; aqui só aperta o espaçamento. */
.station-row-name--caps {
  letter-spacing: -0.01em;
}

.rejected-symbols-container {
  background-color: #fce9ea;
  border-radius: 6px;
}

@media screen and (max-width: 767px) {
  .two-line-ellipsis {
    -webkit-line-clamp: 2;
    line-clamp: 2;
    max-width: none;
  }

  .mobile-avatar {
    width: 14px !important;
    height: 14px !important;
    min-width: 14px !important;
  }

  .mobile-avatar span {
    font-size: 7px !important;
  }

  .group-buttons .v-icon {
    font-size: 12px !important;
  }

  .group-buttons :deep(svg) {
    width: 12px !important;
    height: 12px !important;
  }
}
</style>
